import React from 'react';
import {
    Box,
    Paper,
    Typography,
    IconButton,
    LinearProgress,
    Button,
    Collapse,
    Divider,
    Tooltip,
    Snackbar,
    Alert,
    Chip,
    CircularProgress,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import { useReportJobs } from '../contexts/ReportJobsContext';

const BackgroundJobsWidget: React.FC = () => {
    const {
        jobs,
        activeJobsCount,
        isMinimized,
        setIsMinimized,
        toastNotification,
        closeToastNotification,
        cancelReportJob,
        dismissReportJob,
        clearFinishedJobs,
        downloadJobFile,
    } = useReportJobs();

    const activeJob = jobs.find((j) => j.status === 'processing');

    return (
        <>
            <Snackbar
                open={toastNotification.open}
                autoHideDuration={7000}
                onClose={closeToastNotification}
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
                <Alert
                    onClose={closeToastNotification}
                    severity={toastNotification.severity}
                    variant="filled"
                    sx={{ width: '100%', boxShadow: 3 }}
                >
                    {toastNotification.message}
                </Alert>
            </Snackbar>

            {jobs.length > 0 && (
                <Paper
                    elevation={8}
                    sx={{
                        position: 'fixed',
                        bottom: 20,
                        right: 20,
                        width: { xs: 'calc(100vw - 32px)', sm: 400 },
                        zIndex: (theme) => theme.zIndex.snackbar - 1,
                        borderRadius: 2,
                        overflow: 'hidden',
                        border: '1px solid',
                        borderColor: 'divider',
                    }}
                >
                    {/* Barra de Título do Widget */}
                    <Box
                        onClick={() => setIsMinimized((prev) => !prev)}
                        sx={{
                            px: 2,
                            py: 1.25,
                            bgcolor: activeJobsCount > 0 ? 'primary.main' : 'grey.900',
                            color: 'primary.contrastText',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            cursor: 'pointer',
                            userSelect: 'none',
                        }}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0 }}>
                            {activeJobsCount > 0 ? (
                                <CircularProgress size={18} thickness={5} sx={{ color: 'inherit', flexShrink: 0 }} />
                            ) : (
                                <CheckCircleOutlineIcon fontSize="small" sx={{ flexShrink: 0 }} />
                            )}
                            <Typography variant="subtitle2" noWrap sx={{ fontWeight: 700 }}>
                                {activeJobsCount > 0
                                    ? isMinimized && activeJob
                                        ? `Gerando PDF: ${activeJob.obraNome} (${activeJob.progress}%)`
                                        : `Gerando ${activeJobsCount} ${activeJobsCount === 1 ? 'relatório' : 'relatórios'} em 2º plano`
                                    : `Relatórios concluídos (${jobs.length})`}
                            </Typography>
                        </Box>

                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }} onClick={(e) => e.stopPropagation()}>
                            <Tooltip title={isMinimized ? 'Expandir painel' : 'Minimizar painel'}>
                                <IconButton
                                    size="small"
                                    onClick={() => setIsMinimized((prev) => !prev)}
                                    sx={{ color: 'inherit' }}
                                >
                                    {isMinimized ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                                </IconButton>
                            </Tooltip>

                            {activeJobsCount === 0 && (
                                <Tooltip title="Fechar todos">
                                    <IconButton size="small" onClick={clearFinishedJobs} sx={{ color: 'inherit' }}>
                                        <CloseIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                            )}
                        </Box>
                    </Box>

                    {/* Barra de progresso compacta quando minimizado */}
                    {isMinimized && activeJob && (
                        <LinearProgress variant="determinate" value={activeJob.progress} sx={{ height: 4 }} />
                    )}

                    {/* Corpo Expandido */}
                    <Collapse in={!isMinimized}>
                        <Box sx={{ maxHeight: 320, overflowY: 'auto', bgcolor: 'background.paper' }}>
                            {jobs.map((job, idx) => (
                                <React.Fragment key={job.id}>
                                    {idx > 0 && <Divider />}
                                    <Box sx={{ p: 2 }}>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1 }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                                                <PictureAsPdfIcon
                                                    fontSize="small"
                                                    color={
                                                        job.status === 'completed'
                                                            ? 'success'
                                                            : job.status === 'error'
                                                            ? 'error'
                                                            : 'primary'
                                                    }
                                                />
                                                <Box sx={{ minWidth: 0 }}>
                                                    <Typography variant="body2" noWrap sx={{ fontWeight: 700 }}>
                                                        {job.obraNome}
                                                    </Typography>
                                                    <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
                                                        {job.reportLabel}
                                                    </Typography>
                                                </Box>
                                            </Box>

                                            {job.status === 'processing' ? (
                                                <Chip
                                                    label={`${job.progress}%`}
                                                    size="small"
                                                    color="primary"
                                                    variant="outlined"
                                                    sx={{ fontWeight: 700, height: 22 }}
                                                />
                                            ) : (
                                                <Tooltip title="Remover da lista">
                                                    <IconButton size="small" onClick={() => dismissReportJob(job.id)}>
                                                        <CloseIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            )}
                                        </Box>

                                        {/* Progresso ou Status */}
                                        {job.status === 'processing' && (
                                            <Box sx={{ mt: 1.25 }}>
                                                <LinearProgress
                                                    variant="determinate"
                                                    value={job.progress}
                                                    sx={{ height: 6, borderRadius: 3, mb: 0.75 }}
                                                />
                                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
                                                    <Typography variant="caption" color="text.secondary" noWrap>
                                                        {job.statusText}
                                                    </Typography>
                                                    <Button
                                                        size="small"
                                                        color="error"
                                                        startIcon={<CancelOutlinedIcon fontSize="small" />}
                                                        onClick={() => cancelReportJob(job.id)}
                                                        sx={{ py: 0, minWidth: 'auto', fontSize: '0.72rem', fontWeight: 600 }}
                                                    >
                                                        Cancelar
                                                    </Button>
                                                </Box>
                                            </Box>
                                        )}

                                        {job.status === 'completed' && (
                                            <Box sx={{ mt: 1.25, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                <Typography variant="caption" color="success.main" sx={{ fontWeight: 600 }}>
                                                    ✓ Pronto para download
                                                </Typography>
                                                <Button
                                                    size="small"
                                                    variant="outlined"
                                                    color="primary"
                                                    startIcon={<DownloadIcon fontSize="small" />}
                                                    onClick={() => downloadJobFile(job.id)}
                                                    sx={{ py: 0.25, fontSize: '0.75rem', fontWeight: 600 }}
                                                >
                                                    Baixar PDF
                                                </Button>
                                            </Box>
                                        )}

                                        {job.status === 'cancelled' && (
                                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>
                                                Geração cancelada pelo usuário.
                                            </Typography>
                                        )}

                                        {job.status === 'error' && (
                                            <Box sx={{ mt: 0.75, display: 'flex', alignItems: 'center', gap: 0.5, color: 'error.main' }}>
                                                <ErrorOutlineIcon fontSize="small" />
                                                <Typography variant="caption" color="error">
                                                    {job.errorMessage || 'Falha ao gerar o PDF.'}
                                                </Typography>
                                            </Box>
                                        )}
                                    </Box>
                                </React.Fragment>
                            ))}
                        </Box>

                        {activeJobsCount > 0 && (
                            <Box sx={{ px: 2, py: 1, bgcolor: 'action.hover', borderTop: '1px solid', borderColor: 'divider' }}>
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                    Você pode navegar e usar outras telas do sistema normalmente enquanto o relatório é preparado.
                                </Typography>
                            </Box>
                        )}
                    </Collapse>
                </Paper>
            )}
        </>
    );
};

export default BackgroundJobsWidget;
